import * as React from 'react'
import { Link } from 'react-router-dom'
import { Award, BookOpen, Check, Circle, ClipboardList, ExternalLink, Eye, EyeOff, Users } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import type {
  Activity,
  Certification,
  CertificationPresentation,
  CertificationTest,
  Presentation,
  StudentCertification,
  Test,
  TestResult,
} from '@/lib/types'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState, ErrorNote, Loading, Page } from '@/components/page'
import { TestRunner } from '@/pages/test-runner'
import { ProgramBadge } from '@/components/program-badge'

/**
 * A person's own program: the decks to walk and the tests to pass.
 *
 * "Program" is the user-facing label only. Every identifier here still says
 * certification, matching the schema — the same label-not-rename split as
 * Topics/`activities` in AGENTS.md.
 *
 * Progress is NOT stored by this page. A deck counts as walked when the team's
 * activity for it is ticked complete — the same record the Topics page writes —
 * and a test counts when a passing `test_results` row exists. The
 * `student_certifications` row is a cache of those two facts, refreshed in the
 * database. So this page reads, and the only thing it writes is a test
 * submission, through app.submit_test_result.
 */
export function CertificationsPage() {
  const { currentTeam, session, isCoach } = useAuth()
  const teamId = currentTeam?.id
  const userId = session?.user?.id
  const [taking, setTaking] = React.useState<Test | null>(null)

  const { data, loading, error, refetch } = useQuery(async () => {
    const empty = {
      certs: [] as Certification[],
      decks: [] as CertificationPresentation[],
      certTests: [] as CertificationTest[],
      presentations: [] as Presentation[],
      tests: [] as Test[],
      activities: [] as Activity[],
      completions: [] as Array<{ activity_id: string }>,
      results: [] as TestResult[],
      standing: [] as StudentCertification[],
    }
    if (!teamId || !userId) return empty

    // Which paths this team runs. Everything else hangs off that, so an empty
    // answer means an empty page rather than eight pointless queries.
    const enabled = await sb()
      .from('team_certifications')
      .select('certification_id')
      .eq('team_id', teamId)
    if (enabled.error) throw enabled.error
    const certIds = (enabled.data ?? []).map((r) => r.certification_id as string)
    if (certIds.length === 0) return empty

    const [certs, decks, certTests, activities, completions, results, standing] = await Promise.all([
      sb().from('certifications').select('*').in('id', certIds).eq('active', true).order('display_order'),
      sb().from('certification_presentations').select('*').in('certification_id', certIds).order('display_order'),
      sb().from('certification_tests').select('*').in('certification_id', certIds).order('display_order'),
      // The team's own topic rows — this is what makes a deck's progress the
      // same record the Topics page already keeps.
      sb().from('activities').select('*').eq('team_id', teamId),
      sb().from('activity_completions').select('activity_id').eq('team_id', teamId).eq('user_id', userId),
      sb().from('test_results').select('*').eq('team_id', teamId).eq('user_id', userId),
      sb().from('student_certifications').select('*').eq('team_id', teamId).eq('user_id', userId),
    ])
    for (const r of [certs, decks, certTests, activities, completions, results, standing]) {
      if (r.error) throw r.error
    }

    const presentationIds = (decks.data ?? []).map((d) => d.presentation_id as string)
    const testIds = (certTests.data ?? []).map((t) => t.test_id as string)
    const [presentations, tests] = await Promise.all([
      presentationIds.length
        ? sb().from('presentations').select('*').in('id', presentationIds)
        : Promise.resolve({ data: [], error: null }),
      testIds.length
        ? sb().from('tests').select('*').in('id', testIds).eq('active', true)
        : Promise.resolve({ data: [], error: null }),
    ])
    if (presentations.error) throw presentations.error
    if (tests.error) throw tests.error

    return {
      certs: (certs.data ?? []) as Certification[],
      decks: (decks.data ?? []) as CertificationPresentation[],
      certTests: (certTests.data ?? []) as CertificationTest[],
      presentations: (presentations.data ?? []) as Presentation[],
      tests: (tests.data ?? []) as Test[],
      activities: (activities.data ?? []) as Activity[],
      completions: (completions.data ?? []) as Array<{ activity_id: string }>,
      results: (results.data ?? []) as TestResult[],
      standing: (standing.data ?? []) as StudentCertification[],
    }
  }, [teamId, userId])

  const doneActivities = React.useMemo(
    () => new Set((data?.completions ?? []).map((c) => c.activity_id)),
    [data?.completions],
  )

  /** The team's activity row for a deck, if it has one. */
  function activityFor(presentationId: string): Activity | undefined {
    return (data?.activities ?? []).find((a) => a.presentation_id === presentationId)
  }

  /**
   * Tick the deck, or un-tick it.
   *
   * This writes the SAME `activity_completions` row the Topics page writes —
   * there is no separate "read the certification deck" record, and there must
   * not be, or the two screens would disagree about the same fact. A trigger in
   * 0037 recomputes the certification when this lands.
   */
  async function toggleWalked(activity: Activity) {
    if (!userId) return
    const done = doneActivities.has(activity.id)
    try {
      const { error: err } = done
        ? await sb()
            .from('activity_completions')
            .delete()
            .eq('activity_id', activity.id)
            .eq('user_id', userId)
        : await sb()
            .from('activity_completions')
            .insert({ activity_id: activity.id, team_id: activity.team_id })
      if (err) throw err
    } catch (err) {
      window.alert(err instanceof Error ? err.message : String(err))
    } finally {
      void refetch()
    }
  }

  /** Show me, or do not show me, on the public wall. */
  async function setPublic(standingId: string, visible: boolean) {
    const { error: err } = await sb().rpc('set_certification_public', {
      p_id: standingId,
      p_visible: visible,
    })
    if (err) window.alert(err.message)
    void refetch()
  }

  /** The best attempt at a test — passing beats not passing, then highest score. */
  function bestResult(testId: string): TestResult | undefined {
    return (data?.results ?? [])
      .filter((r) => r.test_id === testId)
      .sort((a, b) => Number(b.passed) - Number(a.passed) || b.score - a.score)[0]
  }

  if (!currentTeam) return null

  return (
    <Page
      title="Programs"
      description="Walk the decks, pass the checks. Your progress is your own — a coach can see the room."
      actions={
        isCoach ? (
          <Button variant="outline" size="sm" asChild>
            <Link to="/certifications/status">
              <Users /> Everyone's status
            </Link>
          </Button>
        ) : undefined
      }
    >
      {error && <ErrorNote error={error} />}
      {loading && !data ? (
        <Loading />
      ) : (data?.certs.length ?? 0) === 0 ? (
        <EmptyState
          icon={Award}
          title="No programs for this team"
          description={
            isCoach
              ? 'Turn one on in Admin — programs are off until a coach enables them for a team.'
              : 'Ask your Don’t Panic Labs coach to turn one on for this team.'
          }
          action={
            isCoach ? (
              <Button asChild>
                <Link to="/admin">Open Admin</Link>
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-8">
          {data!.certs.map((cert) => {
            const deckRows = data!.decks
              .filter((d) => d.certification_id === cert.id)
              .map((d) => ({
                row: d,
                presentation: data!.presentations.find((p) => p.id === d.presentation_id),
              }))
              .filter((d): d is { row: CertificationPresentation; presentation: Presentation } =>
                Boolean(d.presentation),
              )
            const testRows = data!.certTests
              .filter((t) => t.certification_id === cert.id)
              .map((t) => data!.tests.find((x) => x.id === t.test_id))
              .filter((t): t is Test => Boolean(t))

            const decksDone = deckRows.filter((d) => {
              const a = activityFor(d.presentation.id)
              return a && doneActivities.has(a.id)
            }).length
            const testsDone = testRows.filter((t) => bestResult(t.id)?.passed).length
            const total = deckRows.length + testRows.length
            const done = decksDone + testsDone
            const standing = data!.standing.find((s) => s.certification_id === cert.id)

            return (
              <section key={cert.id} className="space-y-3">
                <div className="flex flex-wrap items-start gap-3">
                  <Award className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base font-semibold leading-tight">{cert.title}</h2>
                    {cert.description && (
                      <p className="mt-0.5 text-sm text-muted-foreground">{cert.description}</p>
                    )}
                  </div>
                  {/* Finishing the work is not the end: a DPL coach approves
                      the certificate (0039). Saying "complete" here the moment
                      the last test passed would promise something the portal
                      has not given yet. */}
                  <Badge
                    variant={standing?.status === 'complete' ? 'default' : 'muted'}
                    className={cn(
                      standing?.status === 'awaiting_approval' && 'border-warning/50 text-warning',
                    )}
                  >
                    {standing?.status === 'complete'
                      ? 'Complete'
                      : standing?.status === 'awaiting_approval'
                        ? 'Waiting for approval'
                        : 'In process'}
                  </Badge>
                </div>

                {/* One bar, because "11 of 36" is the only number anyone asks
                    for and it has to be visible without counting rows. */}
                {standing?.status === 'complete' && (
                  <div className="flex flex-wrap items-center gap-3 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm">
                    <span className="min-w-0 flex-1">
                      <b>Approved.</b> You can list your name publicly at{' '}
                      <a href="/certified" className="underline underline-offset-2" target="_blank" rel="noreferrer">
                        /certified
                      </a>
                      {' '}— off unless you turn it on, and only your name and the month are shown.
                    </span>
                    <Button
                      size="sm"
                      variant={standing.public_visible ? 'default' : 'outline'}
                      onClick={() => void setPublic(standing.id, !standing.public_visible)}
                    >
                      {standing.public_visible ? <Eye /> : <EyeOff />}
                      {standing.public_visible ? 'Listed publicly' : 'Not listed'}
                    </Button>
                    {standing.public_visible && (
                      <Button size="sm" variant="ghost" asChild>
                        <a href="/certified" target="_blank" rel="noreferrer">
                          <ExternalLink /> View
                        </a>
                      </Button>
                    )}
                  </div>
                )}

                {standing?.status === 'awaiting_approval' && (
                  <div className="rounded-md border border-warning/50 bg-warning/5 px-3 py-2 text-sm">
                    <b>Every step is done.</b> A DPL coach reviews it from here — you will see this
                    turn to Complete once they approve it.
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary transition-[width]"
                      style={{ width: `${total ? Math.round((done / total) * 100) : 0}%` }}
                    />
                  </div>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {done} of {total}
                  </span>
                </div>

                <Card>
                  <CardContent className="divide-y p-0">
                    {deckRows.map(({ presentation }, i) => {
                      const activity = activityFor(presentation.id)
                      const walked = activity ? doneActivities.has(activity.id) : false
                      const test = testRows.find((t) => t.presentation_id === presentation.id)
                      const result = test ? bestResult(test.id) : undefined

                      const perfect = Boolean(result && result.score === result.total && result.total > 0)

                      return (
                        <div key={presentation.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                          {/* The tick is the control, not just an indicator:
                              "did I get through this deck" is the question this
                              page is asked most, and the answer used to live on
                              another screen. */}
                          <button
                            type="button"
                            onClick={() => activity && void toggleWalked(activity)}
                            disabled={!activity}
                            aria-label={walked ? 'Mark deck as not read' : 'Mark deck as read'}
                            title={walked ? 'You read this deck. Click to undo.' : 'Mark this deck as read'}
                            className={cn(
                              'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors',
                              walked
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'text-muted-foreground hover:border-primary hover:text-primary',
                              !activity && 'opacity-40',
                            )}
                          >
                            {walked ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-3 w-3" />}
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium leading-tight">
                              <span className="tabular-nums text-muted-foreground">{i + 1}. </span>
                              {presentation.title}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-1.5 truncate text-xs text-muted-foreground">
                              <span>{presentation.slide_count} slides</span>
                              <span>·</span>
                              <span className={cn(walked && 'text-primary')}>
                                {walked ? 'Read' : 'Not read yet'}
                              </span>
                              {test && (
                                <>
                                  <span>·</span>
                                  {result?.passed ? (
                                    <span className="font-medium text-primary">
                                      {perfect
                                        ? `Passed — all ${result.total} correct`
                                        : `Passed ${result.score}/${result.total}`}
                                    </span>
                                  ) : result ? (
                                    <span className="text-warning">
                                      Not passed — {result.score}/{result.total}
                                    </span>
                                  ) : (
                                    <span>Test not taken</span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>

                          <div className="flex shrink-0 items-center gap-2">
                            {activity ? (
                              <Button variant="outline" size="sm" asChild>
                                <Link to={`/activities/${activity.id}`}>
                                  <BookOpen /> {walked ? 'Review' : 'Open'}
                                </Link>
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                No topic on this team yet
                              </span>
                            )}
                            {test && (
                              <Button
                                variant={result?.passed ? 'ghost' : 'default'}
                                size="sm"
                                onClick={() => setTaking(test)}
                                // Walking the deck first is the point of the
                                // path; the test is still available, because
                                // someone may already know the material.
                                title={walked ? undefined : 'You have not marked the deck complete yet'}
                              >
                                <ClipboardList />
                                {result?.passed ? 'Retake' : 'Take test'}
                              </Button>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </CardContent>
                </Card>

                {/* Tests that check no single deck — rare, but the schema allows
                    a test to stand alone, so the page must not hide one. */}
                {testRows.filter((t) => !t.presentation_id).length > 0 && (
                  <Card>
                    <CardContent className="divide-y p-0">
                      {testRows
                        .filter((t) => !t.presentation_id)
                        .map((t) => {
                          const result = bestResult(t.id)
                          return (
                            <div key={t.id} className="flex items-center gap-3 px-4 py-3">
                              <StepMark done={Boolean(result?.passed)} />
                              <div className="min-w-0 flex-1 text-sm font-medium">{t.title}</div>
                              <Button size="sm" onClick={() => setTaking(t)}>
                                <ClipboardList /> {result?.passed ? 'Retake' : 'Take test'}
                              </Button>
                            </div>
                          )
                        })}
                    </CardContent>
                  </Card>
                )}

                {/* The badge, on the line after the steps — the reward sits
                    where the work was. Only once a coach approved it: the QR
                    verifies against `complete`, so a badge shown earlier would
                    scan to "could not verify". */}
                {standing?.status === 'complete' && (
                  <div className="flex flex-wrap items-end gap-3">
                    <ProgramBadge
                      title={cert.title}
                      slug={cert.slug}
                      completedAt={standing.approved_at ?? standing.completed_at}
                      badgeId={standing.badge_id}
                      className="max-w-[320px]"
                    />
                    <Button size="sm" variant="ghost" asChild>
                      <a href={`/badges/${standing.badge_id}`} target="_blank" rel="noreferrer">
                        <ExternalLink /> Verification page
                      </a>
                    </Button>
                  </div>
                )}
              </section>
            )
          })}
        </div>
      )}

      {taking && teamId && (
        <TestRunner
          test={taking}
          teamId={teamId}
          onClose={() => setTaking(null)}
          onSubmitted={() => void refetch()}
        />
      )}
    </Page>
  )
}

function StepMark({ done }: { done: boolean }) {
  return (
    <span
      className={cn(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px]',
        done ? 'border-primary bg-primary text-primary-foreground' : 'text-muted-foreground',
      )}
    >
      {done ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-3 w-3" />}
    </span>
  )
}
